import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useMemo } from 'react';
import { Box, Card, CardContent, CardMedia, Button, IconButton, Typography, Skeleton, Modal, } from '@mui/material';
import { ArrowBackIos, ArrowForwardIos, DirectionsBoat } from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { useSwipeable } from 'react-swipeable';
import { useTranslation } from 'react-i18next';
import BookingDetails from './BookingDetails';
export default function BookingCard({ title, images, description, boatId, customerId, }) {
    const { t } = useTranslation();
    const [activeStep, setActiveStep] = useState(0);
    const maxSteps = images.length;
    const [searchParams] = useSearchParams();
    const primaryColor = searchParams.get('primaryColor') || '#1976D2';
    const backgroundColor = searchParams.get('backgroundColor') || '#FFFFFF';
    const secondaryColor = searchParams.get('secondaryColor') || '#4a4a4a';
    const refAffiliate = searchParams.get('ref') || '';
    // Track image load states
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageError, setImageError] = useState(false);
    // Controls rendering of BookingDetails in a modal
    const [showBookingDetails, setShowBookingDetails] = useState(false);
    // Preload images
    useEffect(() => {
        images.forEach((src) => {
            const img = new Image();
            img.src = src;
        });
    }, [images]);
    // Reset image load states when active step changes
    useEffect(() => {
        setImageLoaded(false);
        setImageError(false);
    }, [activeStep]);
    const handleNext = (event) => {
        event.stopPropagation();
        setActiveStep((prev) => (prev + 1) % maxSteps);
    };
    const handleBack = (event) => {
        event.stopPropagation();
        setActiveStep((prev) => (prev - 1 + maxSteps) % maxSteps);
    };
    // Swipe handlers
    const handlers = useSwipeable({
        onSwipedLeft: () => setActiveStep((prev) => (prev + 1) % maxSteps),
        onSwipedRight: () => setActiveStep((prev) => (prev - 1 + maxSteps) % maxSteps),
        preventScrollOnSwipe: true,
        trackMouse: true,
    });
    const handleBooking = (event) => {
        event.stopPropagation();
        setShowBookingDetails(true);
    };
    const handleCloseBookingDetails = () => {
        setShowBookingDetails(false);
    };
    const boats = useMemo(() => [], []);
    return (_jsxs(_Fragment, { children: [_jsx(Box, { sx: {
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                }, children: _jsxs(Card, { sx: {
                        position: 'relative',
                        display: 'flex',
                        maxWidth: '400px',
                        flexDirection: 'column',
                        borderRadius: 3,
                        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                        transition: 'transform 0.3s',
                        width: '100%',
                        '&:hover': {
                            transform: 'translateY(-5px)',
                            cursor: 'pointer',
                        },
                        border: '1px solid rgba(0,0,0,0.07)',
                        backgroundColor: 'var(--primaryBgColor)',
                    }, onClick: handleBooking, children: [_jsxs(Box, Object.assign({}, handlers, { sx: {
                                position: 'relative',
                                overflow: 'hidden',
                                borderTopLeftRadius: 12,
                                borderTopRightRadius: 12,
                                userSelect: 'none',
                                height: { xs: 160, sm: 220 },
                            }, children: [!imageLoaded && !imageError && (_jsx(Skeleton, { variant: "rectangular", width: "100%", height: "100%", sx: { position: 'absolute', top: 0, left: 0, zIndex: 1 } })), !imageError ? (_jsx(CardMedia, { component: "img", loading: "lazy", image: images[activeStep], alt: title, onLoad: () => setImageLoaded(true), onError: () => setImageError(true), sx: {
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                        transition: 'opacity 0.5s ease-in-out',
                                        opacity: imageLoaded ? 1 : 0,
                                    } })) : (_jsx(Box, { sx: {
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        height: '100%',
                                        color: 'rgba(0, 0, 0, 0.5)',
                                    }, children: _jsx(DirectionsBoat, { sx: { fontSize: { xs: 50, sm: 80 } } }) })), maxSteps > 1 && (_jsxs(_Fragment, { children: [_jsx(IconButton, { onClick: handleBack, sx: {
                                                position: 'absolute',
                                                top: '50%',
                                                left: '10px',
                                                transform: 'translateY(-50%)',
                                                zIndex: 2,
                                                backgroundColor: 'rgba(255,255,255,0.7)',
                                                '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' },
                                            }, children: _jsx(ArrowBackIos, {}) }), _jsx(IconButton, { onClick: handleNext, sx: {
                                                position: 'absolute',
                                                top: '50%',
                                                right: '10px',
                                                transform: 'translateY(-50%)',
                                                zIndex: 2,
                                                backgroundColor: 'rgba(255,255,255,0.7)',
                                                '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' },
                                            }, children: _jsx(ArrowForwardIos, {}) })] }))] })), _jsxs(CardContent, { sx: {
                                padding: { xs: 1.5, sm: 2 },
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                backgroundColor: 'var(--primaryBgColor)',
                            }, children: [_jsxs(Box, { children: [_jsx(Typography, { variant: "h6", sx: {
                                                fontWeight: 'bold',
                                                mb: { xs: 0.5, sm: 1 },
                                                color: 'var(--secondaryFgColor)!important',
                                                whiteSpace: 'nowrap', // Prevents line breaks
                                                overflow: 'hidden', // Hides overflowed text
                                                textOverflow: 'ellipsis', // Adds "..."
                                                maxWidth: '100%', // Ensures it respects container width
                                                display: 'block', // Ensures proper rendering inside Tooltip
                                            }, children: title }), _jsx(Typography, { variant: "body2", sx: {
                                                mb: { xs: 1, sm: 2 },
                                                display: '-webkit-box',
                                                WebkitLineClamp: { xs: 2, sm: 3 },
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden',
                                                color: 'var(--secondaryFgColor)!important',
                                            }, children: description })] }), _jsx(Button, { fullWidth: true, onClick: handleBooking, sx: {
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        fontWeight: 'bold',
                                        backgroundColor: 'var(--primaryFgColor)!important',
                                        color: 'var(--primaryBgColor)!important',
                                        '&:hover': {
                                            backgroundColor: 'var(--primaryFgColor)!important',
                                            opacity: 0.8,
                                        },
                                    }, children: t('button.bookNow') })] })] }) }), _jsx(Modal, { open: showBookingDetails, onClose: handleCloseBookingDetails, "aria-labelledby": "booking-details-modal", "aria-describedby": "booking-details-description", children: _jsx(Box, { sx: {
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: { xs: '95%', md: '90%' },
                        maxWidth: 1900,
                        height: { xs: '95%', md: '90%' },
                        maxHeight: 900,
                        bgcolor: 'var(--primaryBgColor)',
                        boxShadow: 24,
                        p: 4,
                        overflowY: 'auto',
                        borderRadius: 2,
                    }, children: _jsx(BookingDetails, { boats: boats, state: {
                            title,
                            images,
                            description,
                            boatId,
                            customerId,
                            primaryColor,
                            backgroundColor,
                            secondaryColor,
                            refAffiliate,
                            handleBack: handleCloseBookingDetails,
                        } }) }) })] }));
}
